import React, { useEffect, useState } from "react";
import { AlertTriangle, KeyRound, Save, ShieldCheck, UploadCloud } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import MenuLateral from "../../Componentes/MenuLateral";
import MenuSuperior from "../../Componentes/MenuSuperior";
import { useOperador } from "../../Hooks/useOperador";
import { operadorEhAdmin } from "../../Utils/cargos";
import { assinaturaDigitalService } from "../../Services/assinaturaDigital";
import "./styles.css";

const formInicial = {
  certificado: null,
  senha: "",
  certificadoTitular: "",
  certificadoCnpj: "",
  certificadoSerial: "",
  validadeCertificado: "",
  observacao: "",
  textosCarimbo: [""],
};

const formatarData = (valor) => {
  if (!valor) return "Nao informado";
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return valor;
  return data.toLocaleDateString("pt-BR");
};

const AssinaturaDigitalCertificado = () => {
  const navigate = useNavigate();
  const { IDOperador, nome, operador } = useOperador();
  const [certificadoAtual, setCertificadoAtual] = useState(null);
  const [form, setForm] = useState(formInicial);
  const [loading, setLoading] = useState(false);
  const [inspecionando, setInspecionando] = useState(false);
  const [inspecaoErro, setInspecaoErro] = useState("");
  const [editandoCarimbosId, setEditandoCarimbosId] = useState(null);
  const [textosEdicao, setTextosEdicao] = useState([""]);
  const podeAdministrar = operadorEhAdmin(operador);

  const carregarCertificado = async () => {
    try {
      setLoading(true);
      const response = await assinaturaDigitalService.buscarCertificado();
      setCertificadoAtual(response);
    } catch (error) {
      console.error("Erro ao carregar certificado ICP-Brasil.", error);
      Swal.fire("Erro", "Nao foi possivel carregar o certificado.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!podeAdministrar) {
      navigate("/Menu");
      return;
    }

    carregarCertificado();
  }, [podeAdministrar, navigate]);

  useEffect(() => {
    if (!form.certificado || !form.senha) {
      setInspecaoErro("");
      setInspecionando(false);
      return;
    }
    let cancelado = false;
    const timer = setTimeout(async () => {
      try {
        setInspecionando(true);
        const dados = await assinaturaDigitalService.inspecionarCertificado(form.certificado, form.senha);
        if (cancelado) return;
        setForm((atual) => ({
          ...atual,
          certificadoTitular: dados.certificadoTitular || atual.certificadoTitular,
          certificadoCnpj: dados.certificadoCnpj || atual.certificadoCnpj,
          certificadoSerial: dados.certificadoSerial || atual.certificadoSerial,
          validadeCertificado: dados.validadeCertificado || atual.validadeCertificado,
        }));
        setInspecaoErro("");
      } catch (error) {
        if (!cancelado) setInspecaoErro(error.response?.data?.errors?.default || "Nao foi possivel ler o certificado.");
      } finally {
        if (!cancelado) setInspecionando(false);
      }
    }, 600);
    return () => { cancelado = true; clearTimeout(timer); };
  }, [form.certificado, form.senha]);

  const atualizarCampo = (campo, valor) => {
    setForm((atual) => ({ ...atual, [campo]: valor }));
  };

  const atualizarTexto = (textos, setTextos, indice, valor) => {
    setTextos(textos.map((texto, i) => i === indice ? valor : texto));
  };

  const editarCarimbos = (certificado) => {
    setEditandoCarimbosId(certificado.IDAssinaturaDigitalCertificado);
    setTextosEdicao(certificado.carimbos?.length
      ? certificado.carimbos.map((carimbo) => carimbo.texto) : [""]);
  };

  const salvarEdicaoCarimbos = async () => {
    const textos = textosEdicao.map((texto) => texto.trim());
    if (textos.some((texto) => !texto)) {
      Swal.fire("Atencao", "Preencha todos os textos de carimbo.", "warning");
      return;
    }
    try {
      setLoading(true);
      await assinaturaDigitalService.atualizarCarimbos(editandoCarimbosId, textos);
      setEditandoCarimbosId(null);
      await carregarCertificado();
      Swal.fire("Sucesso", "Carimbos atualizados.", "success");
    } catch (error) {
      Swal.fire("Erro", error.response?.data?.errors?.default || "Nao foi possivel atualizar os carimbos.", "error");
    } finally {
      setLoading(false);
    }
  };

  const salvar = async () => {
    if (!form.certificado) {
      Swal.fire("Atencao", "Selecione o arquivo do certificado.", "warning");
      return;
    }

    if (!form.senha) {
      Swal.fire("Atencao", "Informe a senha do certificado.", "warning");
      return;
    }

    if (form.textosCarimbo.some((texto) => !texto.trim())) {
      Swal.fire("Atencao", "Preencha todos os textos de carimbo.", "warning");
      return;
    }

    try {
      setLoading(true);
      await assinaturaDigitalService.salvarCertificado(form);
      setForm(formInicial);
      setInspecaoErro("");
      await carregarCertificado();
      Swal.fire("Sucesso", "Certificado atualizado com sucesso.", "success");
    } catch (error) {
      console.error("Erro ao salvar certificado ICP-Brasil.", error);
      Swal.fire(
        "Erro",
        error.response?.data?.errors?.default ||
          "Nao foi possivel salvar o certificado.",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const desativar = async (certificado) => {
    const confirmacao = await Swal.fire({
      title: "Desativar certificado A1?",
      text: `O certificado ${certificado.certificadoTitular || certificado.IDAssinaturaDigitalCertificado} deixara de aparecer nas assinaturas futuras.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Desativar",
      cancelButtonText: "Cancelar",
    });
    if (!confirmacao.isConfirmed) return;

    try {
      setLoading(true);
      await assinaturaDigitalService.desativarCertificado(
        certificado.IDAssinaturaDigitalCertificado,
      );
      await carregarCertificado();
      Swal.fire("Sucesso", "Certificado desativado.", "success");
    } catch (error) {
      Swal.fire("Erro", error.response?.data?.errors?.default ||
        "Nao foi possivel desativar o certificado.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="menu-container-menu certificado-page">
      <MenuLateral />
      <div id="painel_principal_menu" align="center">
        <div className="page-shell certificado-shell">
          <MenuSuperior userId={String(IDOperador).padStart(6, "0")} userName={nome} />

          <section className="certificado-header">
            <div>
              <span><KeyRound size={17} /> ICP-Brasil</span>
              <h1>Certificado ICP-Brasil</h1>
            </div>
            <button type="button" onClick={() => navigate("/AssinaturaDigital/Certificado/ControleAcesso")}>Controle de Acesso</button>
          </section>

          <section className="certificado-security">
            <ShieldCheck size={30} />
            <div>
              <h2>Seu certificado esta protegido.</h2>
              <p>
                Por seguranca, a senha e o arquivo do certificado nao ficam
                visiveis para os usuarios do sistema.
              </p>
              <small>
                Somente administradores podem cadastrar ou desativar
                certificados A1.
              </small>
            </div>
          </section>

          <section className="certificado-grid">
            <article className="certificado-status">
              <header>
                <h2>Certificados A1 ativos</h2>
                <span className={certificadoAtual?.configurado ? "is-ok" : "is-pending"}>
                  {certificadoAtual?.certificados?.length || 0} ativo(s)
                </span>
              </header>

              {loading && <p>Carregando certificado...</p>}

              {!loading && certificadoAtual?.certificados?.length > 0 && (
                <dl>
                  {certificadoAtual.certificados.map((certificado) => (
                    <div key={certificado.IDAssinaturaDigitalCertificado}>
                      <dt>A1 #{certificado.IDAssinaturaDigitalCertificado}</dt>
                      <dd>{certificado.certificadoTitular || "Titular nao informado"}</dd>
                      <small>CNPJ: {certificado.certificadoCnpj || "Nao informado"}</small>
                      <small>Serial: {certificado.certificadoSerial || "Nao informado"}</small>
                      <small>Validade: {formatarData(certificado.validadeCertificado)}</small>
                      <small>Cadastrado em: {formatarData(certificado.dataCadastro)}</small>
                      <small>Carimbos: {certificado.carimbos?.length || 0}</small>
                      {editandoCarimbosId === certificado.IDAssinaturaDigitalCertificado ? (
                        <div className="certificado-carimbos-editor">
                          {textosEdicao.map((texto, indice) => (
                            <label key={indice}>Carimbo {indice + 1}
                              <textarea rows={3} value={texto} maxLength={1000}
                                onChange={(event) => atualizarTexto(textosEdicao, setTextosEdicao, indice, event.target.value)} />
                              {textosEdicao.length > 1 && <button type="button" onClick={() => setTextosEdicao(textosEdicao.filter((_, i) => i !== indice))}>Remover texto</button>}
                            </label>
                          ))}
                          <button type="button" onClick={() => setTextosEdicao([...textosEdicao, ""])} disabled={textosEdicao.length >= 20}>Adicionar texto</button>
                          <button type="button" onClick={salvarEdicaoCarimbos} disabled={loading}>Salvar carimbos</button>
                          <button type="button" onClick={() => setEditandoCarimbosId(null)}>Cancelar</button>
                        </div>
                      ) : <button type="button" onClick={() => editarCarimbos(certificado)}>Editar carimbos</button>}
                      <button type="button" onClick={() => desativar(certificado)}>
                        Desativar certificado
                      </button>
                    </div>
                  ))}
                </dl>
              )}

              {!loading && !certificadoAtual?.configurado && (
                <div className="certificado-empty">
                  <AlertTriangle size={22} />
                  <span>Nenhum certificado cadastrado para assinatura digital.</span>
                </div>
              )}
            </article>

            <article className="certificado-form">
              <header>
                <h2>Cadastrar certificado A1</h2>
                <p>Adicione outro certificado .pfx ou .p12 sem substituir os ativos.</p>
              </header>

              <label className="certificado-file">
                <UploadCloud size={24} />
                <span>
                  {form.certificado?.name || "Selecionar certificado .pfx ou .p12"}
                </span>
                <input
                  type="file"
                  accept=".pfx,.p12"
                  onChange={(event) =>
                    setForm((atual) => ({ ...atual, certificado: event.target.files?.[0] || null,
                      certificadoTitular: "", certificadoCnpj: "", certificadoSerial: "", validadeCertificado: "" }))
                  }
                />
              </label>

              {inspecionando && <p>Lendo dados do certificado...</p>}
              {inspecaoErro && <p className="certificado-inspecao-erro">{inspecaoErro}</p>}

              <label>
                Senha do certificado
                <input
                  type="password"
                  value={form.senha}
                  onChange={(event) => atualizarCampo("senha", event.target.value)}
                  autoComplete="new-password"
                />
              </label>

              <div className="certificado-form__columns">
                <label>
                  Titular
                  <input
                    value={form.certificadoTitular}
                    onChange={(event) =>
                      atualizarCampo("certificadoTitular", event.target.value)
                    }
                    placeholder="Pentagrama..."
                  />
                </label>
                <label>
                  CNPJ
                  <input
                    value={form.certificadoCnpj}
                    onChange={(event) =>
                      atualizarCampo("certificadoCnpj", event.target.value)
                    }
                  />
                </label>
              </div>

              <div className="certificado-form__columns">
                <label>
                  Serial
                  <input
                    value={form.certificadoSerial}
                    onChange={(event) =>
                      atualizarCampo("certificadoSerial", event.target.value)
                    }
                  />
                </label>
                <label>
                  Validade
                  <input
                    type="date"
                    value={form.validadeCertificado}
                    onChange={(event) =>
                      atualizarCampo("validadeCertificado", event.target.value)
                    }
                  />
                </label>
              </div>

              <label>
                Observacao
                <textarea
                  rows={3}
                  value={form.observacao}
                  onChange={(event) =>
                    atualizarCampo("observacao", event.target.value)
                  }
                />
              </label>

              <div className="certificado-carimbos-editor">
                <strong>Textos de carimbo visivel</strong>
                <small>Um texto e aplicado automaticamente; varios permitem escolher ao assinar.</small>
                {form.textosCarimbo.map((texto, indice) => (
                  <label key={indice}>Carimbo {indice + 1}
                    <textarea rows={3} value={texto} maxLength={1000}
                      onChange={(event) => atualizarCampo("textosCarimbo", form.textosCarimbo.map((item, i) => i === indice ? event.target.value : item))} />
                    {form.textosCarimbo.length > 1 && <button type="button" onClick={() => atualizarCampo("textosCarimbo", form.textosCarimbo.filter((_, i) => i !== indice))}>Remover texto</button>}
                  </label>
                ))}
                <button type="button" onClick={() => atualizarCampo("textosCarimbo", [...form.textosCarimbo, ""])} disabled={form.textosCarimbo.length >= 20}>Adicionar outro texto</button>
              </div>

              <footer>
                <button type="button" onClick={salvar} disabled={loading || inspecionando || !!inspecaoErro}>
                  <Save size={17} />
                  Salvar certificado
                </button>
              </footer>
            </article>
          </section>
        </div>
      </div>
    </div>
  );
};

export default AssinaturaDigitalCertificado;
